import { useState } from "react";
import axios from "axios";
import { CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "h-12 w-full border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 transition-colors focus:border-white/45 focus:outline-none";
const labelCls = "mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/45";

export function EnquiryForm({ source = "website", compact = false }) {
    const [form, setForm] = useState({
        name: "", business: "", email: "", phone: "", industry: "",
        current_website: "", main_challenge: "", desired_outcome: "",
        budget: "", preferred_contact: "Email",
    });
    const [state, setState] = useState("idle");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setState("sending");
        try {
            await axios.post(`${API}/enquiries`, { ...form, source });
            setState("success");
        } catch {
            setState("error");
        }
    };

    if (state === "success") {
        return (
            <div className="border border-white/15 bg-elev p-10 text-center" data-testid="enquiry-success">
                <CheckCircle size={40} weight="fill" className="mx-auto text-white" />
                <p className="mt-5 font-display text-2xl font-bold text-white">Enquiry received.</p>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/60">
                    Your details are in the HIGHTS system and on their way to the team.
                    A confirmation is heading to your inbox — we'll be in touch shortly.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="enquiry-form">
            <div>
                <label className={labelCls} htmlFor="enq-name">Name *</label>
                <input id="enq-name" data-testid="enquiry-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
            </div>
            <div>
                <label className={labelCls} htmlFor="enq-business">Business</label>
                <input id="enq-business" data-testid="enquiry-business" value={form.business} onChange={set("business")} className={inputCls} placeholder="Business name" />
            </div>
            <div>
                <label className={labelCls} htmlFor="enq-email">Email *</label>
                <input id="enq-email" data-testid="enquiry-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
            </div>
            <div>
                <label className={labelCls} htmlFor="enq-phone">Phone</label>
                <input id="enq-phone" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+44…" />
            </div>
            <div>
                <label className={labelCls} htmlFor="enq-industry">Industry</label>
                <input id="enq-industry" data-testid="enquiry-industry" value={form.industry} onChange={set("industry")} className={inputCls} placeholder="e.g. Property, hospitality, trades" />
            </div>
            <div>
                <label className={labelCls} htmlFor="enq-website">Current website</label>
                <input id="enq-website" data-testid="enquiry-website" value={form.current_website} onChange={set("current_website")} className={inputCls} placeholder="https://… (if you have one)" />
            </div>
            {!compact && (
                <>
                    <div>
                        <label className={labelCls} htmlFor="enq-budget">Budget</label>
                        <select id="enq-budget" data-testid="enquiry-budget" value={form.budget} onChange={set("budget")} className={inputCls}>
                            <option value="">Select a range…</option>
                            <option value="under-1000">Under £1,000</option>
                            <option value="1000-5000">£1,000 – £5,000</option>
                            <option value="5000-15000">£5,000 – £15,000</option>
                            <option value="15000-plus">£15,000+</option>
                            <option value="not-sure">Not sure yet</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="enq-contact">Preferred contact method</label>
                        <select id="enq-contact" data-testid="enquiry-preferred-contact" value={form.preferred_contact} onChange={set("preferred_contact")} className={inputCls}>
                            <option>Email</option>
                            <option>Phone</option>
                            <option>WhatsApp</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelCls} htmlFor="enq-challenge">Main challenge</label>
                        <textarea id="enq-challenge" data-testid="enquiry-challenge" rows={3} value={form.main_challenge} onChange={set("main_challenge")}
                            className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-white placeholder:text-white/30 transition-colors focus:border-white/45 focus:outline-none"
                            placeholder="What's not working right now? Missed enquiries, an outdated website, too much admin…" />
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelCls} htmlFor="enq-outcome">Desired outcome</label>
                        <textarea id="enq-outcome" data-testid="enquiry-outcome" rows={3} value={form.desired_outcome} onChange={set("desired_outcome")}
                            className="w-full border border-white/15 bg-black px-4 py-3 text-sm text-white placeholder:text-white/30 transition-colors focus:border-white/45 focus:outline-none"
                            placeholder="What would success look like 6 months from now?" />
                    </div>
                </>
            )}
            <div className="md:col-span-2">
                <button
                    type="submit"
                    disabled={state === "sending"}
                    data-testid="enquiry-submit-button"
                    className="w-full bg-white py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white/85 disabled:opacity-50"
                >
                    {state === "sending" ? "Sending…" : "Send my enquiry"}
                </button>
                {state === "error" && (
                    <p className="mt-3 text-center text-xs text-white/50" data-testid="enquiry-error">
                        Something went wrong — please try again or WhatsApp us instead.
                    </p>
                )}
                <p className="mt-3 text-center text-[11px] text-white/35">
                    Enquiries go straight into the HIGHTS CRM and to the team by email.
                </p>
            </div>
        </form>
    );
}
